import { useState, useEffect, useRef } from "react";
import logo from "../../assets/images/ecom_tamil_logo.png";
import { Mail, Lock, ArrowRight, ChevronLeft, Eye, EyeOff, User, Wifi, WifiOff, Shield } from "lucide-react";
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import InputField from "../../components/InputField";
import Button from "../../components/Button";
import { postRequest } from "../../utils/networking";
import { apiUrls } from "../../utils/url";
import CommonToastContainer from "../../components/CommonToastContainer";
import websocketService from "../../utils/websocket";
import ConfirmationPopup from "../../components/ConfirmationPopup";

export default function UserLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleUserData, setGoogleUserData] = useState(null);
  const [wsStatus, setWsStatus] = useState("disconnected");
  const passwordRef = useRef(null);

  useEffect(() => {
    if (location.state?.message) {
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Check for pre-filled email from signup - immediately set email
  useEffect(() => {
    const prefilledEmail = sessionStorage.getItem('prefillEmail');
    if (prefilledEmail) {
      setEmail(prefilledEmail);
      setStep("login"); // Automatically go to login step
      // Clear it after using so it doesn't persist for next login
      sessionStorage.removeItem('prefillEmail');

      // Show a message
      setTimeout(() => {
        showToast("Account created! Please login with your password.", "info");
      }, 100);
    }
  }, []);

  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message);
    } else if (type === "error") {
      toast.error(message, {});
    } else if (type === "info") {
      toast.info(message, {});
    } else if (type === "warning") {
      toast.warning(message, {});
    }
  };

  useEffect(() => {
    const handleConnect = () => {
      console.log("WebSocket connected in login page");
      setWsStatus("connected");
    };

    const handleDisconnect = (data) => {
      console.log("WebSocket disconnected in login page:", data);
      setWsStatus("disconnected");
    };

    websocketService.on("connect", handleConnect);
    websocketService.on("disconnected", handleDisconnect);

    return () => {
      websocketService.off("connect", handleConnect);
      websocketService.off("disconnected", handleDisconnect);
    };
  }, []);

  // Autofocus password field when step changes to 'login'
  useEffect(() => {
    if (step === "login") {
      setTimeout(() => {
        passwordRef.current?.focus();
      }, 100);
    }
  }, [step]);

  const connectWebSocket = async (authToken, userId) => {
    try {
      console.log("🔌 Connecting WebSocket after successful login...");
      setWsStatus("connecting");
      await websocketService.connect(authToken, userId);
      console.log("✅ WebSocket connected successfully");
      setWsStatus("connected");
      return true;
    } catch (error) {
      console.error("❌ WebSocket connection failed:", error);
      setWsStatus("disconnected");
      return false;
    }
  };

  const completeLogin = async (loginData) => {
    // Ensure usertype is correctly captured and default to "2" if missing
    const usertype = (loginData.userData.usertype !== undefined && loginData.userData.usertype !== null)
      ? loginData.userData.usertype.toString()
      : "2";

    // Update userData with consistent usertype and role for header display
    const updatedUserData = {
      ...loginData.userData,
      usertype: usertype,
      role: usertype === "1" ? "Admin" : (usertype === "3" ? "Staff" : "Student")
    };

    localStorage.setItem("user", JSON.stringify(updatedUserData));
    localStorage.setItem("authToken", loginData.token);
    localStorage.setItem("usertype", usertype);

    showToast("Login successful! Redirecting...", "success");

    const redirectPath = localStorage.getItem('redirectAfterLogin');

    // Set timeout for redirection
    setTimeout(() => {
      // If we have a specific redirect path and it's not a restricted/root page
      if (redirectPath &&
        redirectPath !== '/login' &&
        redirectPath !== '/signin' &&
        redirectPath !== '/') {
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        // Default redirection based on usertype
        if (usertype === "1") {
          navigate("/admin/courseslist");
        } else if (usertype === "3") {
          navigate("/solutions");
        } else {
          navigate("/courses");
        }
      }
    }, 1500);
  };

  const loginWithGoogle = useGoogleLogin({
    flow: 'implicit',
    ux_mode: 'popup',
    redirect_uri: window.location.origin + "/login",
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      const accessToken = tokenResponse.access_token;

      try {
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });

        const userInfo = await userInfoResponse.json();
        const userName = userInfo.name;
        const userEmail = userInfo.email;

        if (userEmail) {
          const response = await postRequest(apiUrls.verifyuser, {
            email: userEmail,
            logintype: 2
          });

          if (response.status === "E" &&
            (response.message?.includes("Session Expired") ||
              response.message?.includes("logged in from another device"))) {
            showToast(response.message, "error");
            return;
          }

          if (response.status === "S" || response.status === "E") {
            const responseData = response.data?.[0] || {};
            const usertype = responseData.usertype || response.usertype || 2;
            
            if (usertype.toString() === "1") {
              showToast("Access denied for this user type", "error");
              return;
            }

            if (usertype.toString() !== "2" && usertype.toString() !== "3") {
              showToast("email is invalid", "error");
              return;
            }

            const storedUsername = localStorage.getItem(`username_${userEmail}`) || responseData.username || userName;
            const userData = {
              id: responseData.userId || response.userId || userEmail,
              name: storedUsername,
              email: userEmail,
              logintype: 2,
              usertype: usertype
            };

            const authToken = response.token || accessToken;
            connectWebSocket(authToken, userData.id);
            await completeLogin({ userData, token: authToken });

          } else if (response.status === "N") {
            // New Google user - auto-register them
            try {
              const registerResponse = await postRequest(apiUrls.registeruser, {
                email: userEmail,
                username: userName,
                logintype: 2,
                app: 2,
                usertype: 2
              });

              if (registerResponse.status === "S" || registerResponse.token) {
                const regData = registerResponse.data?.[0] || {};
                const regUsertype = regData.usertype || registerResponse.usertype || 2;
                const regToken = registerResponse.token || accessToken;
                const userData = {
                  id: regData.userId || registerResponse.userId || userEmail,
                  name: regData.username || userName,
                  email: userEmail,
                  logintype: 2,
                  usertype: regUsertype
                };
                connectWebSocket(regToken, userData.id);
                await completeLogin({ userData, token: regToken });
              } else {
                showToast(registerResponse.message || "Unable to create account. Please try again.", "error");
              }
            } catch (regError) {
              console.error("Google auto-register error:", regError);
              showToast("Unable to create account. Please try again.", "error");
            }
            return;
          } else {
            showToast("Unable to verify email. Please try again.", "error");
          }
        }
      } catch (error) {
        console.error("Google login error:", error);
        if (!error.message?.includes("Session Expired") &&
          !error.message?.includes("403")) {
          showToast("Failed to login with Google. Please try again.", "error");
        }
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setIsLoading(false);
      showToast("Google login failed! Please try again.", "error");
    },
  });

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!email) return;

    setError("");
    setIsLoading(true);

    try {
      const response = await postRequest(apiUrls.verifyuser, {
        email: email,
        logintype: 1,
        app: 2,
      });

      if (response.status === "E" &&
        (response.message?.includes("Session Expired") ||
          response.message?.includes("logged in from another device"))) {
        showToast(response.message, "error");
        return;
      }

      if (response.status === "S" || response.status === "E") {
        const responseData = response.data?.[0] || {};
        const usertype = responseData.usertype || response.usertype || null;
        
        if (usertype !== null) {
          if (usertype.toString() === "1") {
            showToast("Access denied for this user type", "error");
            return;
          }
          if (usertype.toString() !== "2" && usertype.toString() !== "3") {
            showToast("email is invalid", "error");
            return;
          }
        }
        
        setStep("login");
      } else if (response.status === "N") {
        // New user - redirect to signup step
        setStep("signup");
        showToast("No account found. Please create one.", "info");
      } else {
        showToast("Unable to verify email. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error checking user:", error);
      if (!error.message?.includes("Session Expired") &&
        !error.message?.includes("403")) {
        showToast("Unable to verify email. Please try again.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    setPassword("");
    setConfirmPassword("");
    setError("");
    setUsername("");
    setGoogleUserData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (step === "login") {
      setIsLoading(true);
      try {
        const verifyResponse = await postRequest(apiUrls.verifypassword, {
          email: email,
          password: password,
          app: 2
        });
        console.log('verifyResponse', verifyResponse)
        if (verifyResponse.status === "E" &&
          (verifyResponse.message?.includes("Session Expired") ||
            verifyResponse.message?.includes("logged in from another device"))) {
          showToast(verifyResponse.message, "error");
          return;
        }

        if (verifyResponse.status === "S" && verifyResponse.token) {
          const responseData = verifyResponse.data?.[0] || {};
          const usertype = responseData.usertype || verifyResponse.usertype || 2;

          if (usertype.toString() === "1") {
            showToast("Access denied for this user type", "error");
            return;
          }

          if (usertype.toString() !== "2" && usertype.toString() !== "3") {
            showToast("email is invalid", "error");
            return;
          }

          const userName = localStorage.getItem(`username_${email}`) ||
            responseData.username ||
            verifyResponse.username ||
            email.split("@")[0];

          const userData = {
            id: responseData.userId || verifyResponse.userId || email,
            name: userName,
            email: email,
            logintype: 1,
            usertype: usertype
          };

          const wsConnected = connectWebSocket(verifyResponse.token, userData.id);

          await completeLogin({ userData, token: verifyResponse.token });

        } else {
          showToast(verifyResponse.message || "Invalid email or password.", "error");
        }
      } catch (error) {
        console.error("Login error:", error);
        if (!error.message?.includes("Session Expired") &&
          !error.message?.includes("403")) {
          showToast("Invalid email or password.", "error");
        }
      } finally {
        setIsLoading(false);
      }
    }
    else if (step === "signup") {
      if (password !== confirmPassword) {
        showToast("Passwords do not match", "error");
        return;
      }
      if (password.length < 6) {
        showToast("Password must be at least 6 characters", "error");
        return;
      }
      if (!username || username.trim() === "") {
        showToast("Username is required", "error");
        return;
      }

      setIsLoading(true);
      try {
        const signupResponse = await postRequest(apiUrls.registeruser, {
          email: email,
          password: password,
          username: username,
          logintype: 1,
          app: 2,
          usertype: 2
        });

        if (signupResponse.status === "S") {
          localStorage.setItem(`username_${email}`, username);

          // Store email to pre-fill on login page if they refresh
          sessionStorage.setItem('prefillEmail', email);

          showToast("Account created successfully! Please login.", "success");

          // Switch to login step directly instead of navigating to the same page
          setTimeout(() => {
            setStep("login");
            setPassword("");
            setConfirmPassword("");
          }, 300);

        } else if (signupResponse.status === "E") {
          showToast("Email already exists. Please login instead.", "error");
          setTimeout(() => setStep("login"), 1500);
        } else {
          showToast(signupResponse.message || "Unable to create account.", "error");
        }
      } catch (error) {
        console.error("Signup error:", error);
        if (!error.message?.includes("Session Expired") &&
          !error.message?.includes("403")) {
          showToast("Unable to create account. Please try again.", "error");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const isEmailDisabled = step === "login" || (step === "signup" && googleUserData);

  useEffect(() => {
    return () => {
      const isLoggedIn = localStorage.getItem("authToken");
      if (!isLoggedIn) {
        websocketService.disconnect();
      }
    };
  }, []);

  return (
    <>
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6 selection:bg-orange-200 selection:text-orange-900">
        <div className="fixed inset-0 overflow-hidden -z-10">
          <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-br from-orange-200 via-orange-100 to-transparent rounded-full blur-3xl opacity-20" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[45%] h-[45%] bg-gradient-to-tr from-amber-200 via-orange-100 to-transparent rounded-full blur-3xl opacity-20" />
        </div>

        <div className="w-full max-w-md">
          <div className="bg-white/90 backdrop-blur-sm border border-slate-100 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <img
                src={logo}
                alt="Logo"
                className="h-12 w-auto object-contain cursor-pointer"
                onClick={() => navigate("/")}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
              <div className="flex items-center gap-2">
                <User className="text-amber-600 w-5 h-7" />
                <h1 className="text-xl font-bold text-amber-600">Login</h1>
              </div>
            </div>
            <div className="relative mb-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-100"></div>
              </div>
            </div>

            <div className="text-center p-5">
              <h1 className="text-2xl font-bold text-gray-900">
                {step === "email" && "Welcome"}
                {step === "login" && "Enter Password"}
                {step === "signup" && "Create Account"}
              </h1>
            </div>

            <form onSubmit={step === "email" ? handleContinue : handleSubmit} className="space-y-4">
              {step === "email" ? (
                <>
                  <InputField
                    icon={Mail}
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={true}
                    disabled={isLoading}
                  />

                  <Button
                    className="cursor-pointer w-full"
                    type="submit"
                    variant="primary"
                    isLoading={isLoading}
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </Button>

                  <div className="relative py-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-3 text-gray-500">Or continue with</span>
                    </div>
                  </div>

                  <Button
                    className="cursor-pointer w-full"
                    type="button"
                    variant="secondary"
                    disabled={isLoading}
                    onClick={loginWithGoogle}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continue With Google
                  </Button>
                </>
              ) : (
                <>
                  <InputField
                    icon={Mail}
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={true}
                    readOnly={isEmailDisabled}
                    className={isEmailDisabled ? "bg-gray-50 text-gray-500" : ""}
                    disabled={isEmailDisabled || isLoading}
                  />

                  {step === "signup" && (
                    <InputField
                      icon={User}
                      type="text"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required={true}
                      disabled={isLoading}
                    />
                  )}

                  <InputField
                    ref={passwordRef}
                    icon={Lock}
                    type={showPassword ? "text" : "password"}
                    placeholder={step === "login" ? "Password" : "Create password (min. 6 chars)"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={true}
                    disabled={isLoading}
                    rightIcon={showPassword ? EyeOff : Eye}
                    onRightIconClick={() => setShowPassword(!showPassword)}
                  />

                  {step === "signup" && (
                    <>
                      <InputField
                        icon={Lock}
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required={true}
                        disabled={isLoading}
                        rightIcon={showPassword ? EyeOff : Eye}
                        onRightIconClick={() => setShowPassword(!showPassword)}
                      />

                      <div className="flex items-center justify-between mt-1">
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 cursor-pointer"
                        >
                          {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          {showPassword ? "Hide" : "Show"} password
                        </button>
                        <p className="text-xs text-gray-400">Password must be at least 6 characters</p>
                      </div>
                    </>
                  )}

                  {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3">
                      <p className="text-red-600 text-xs">{error}</p>
                    </div>
                  )}

                  {step === "login" && (
                    <div className="text-right">
                      <button
                        type="button"
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                        onClick={() => showToast("Password reset link would be sent to your email", "info")}
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 pt-2">
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={isLoading}
                      className="cursor-pointer w-full"
                    >
                      {step === "login" ? "Sign In" : "Create Account"}
                    </Button>

                    <Button
                      type="button"
                      variant="text"
                      onClick={handleBack}
                      className="cursor-pointer"
                      disabled={isLoading}
                    >
                      <ChevronLeft className="w-3 h-3 text-amber-600" />
                      Use different email
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      <CommonToastContainer />
    </>
  );
}